import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import {
  createEventType,
  deleteEventType,
  getEventTypes,
  updateEventType,
} from '../api/event-typeAPI';

const sortTypes = (items) =>
  [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }));

const EventTypeLayer = () => {
  const userProfile = useUserProfile();
  const [eventTypes, setEventTypes] = useState([]);
  const [formState, setFormState] = useState({ code: '', name: '', eventAppIcon: '', iconColor: '' });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const validationFields = [
    { field: 'code', label: 'Code', required: true },
    { field: 'name', label: 'Name', required: true },
  ];
  const { errors, setErrors, validate } = useValidator(validationFields, formState);

  const resetDataTable = (fullyDestroy = false) => {
    if (dtRef.current) {
      dtRef.current.destroy(fullyDestroy);
      dtRef.current = null;
    }
  };

  const fetchEventTypes = async () => {
    try {
      const data = await getEventTypes();
      setEventTypes(sortTypes(data || []));
    } catch (error) {
      console.error('Error loading event types', error);
    }
  };

  useEffect(() => {
    fetchEventTypes();
  }, []);

  useEffect(() => {
    resetDataTable();

    if (eventTypes.length) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => resetDataTable(true);
  }, [eventTypes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const resetForm = () => {
    setFormState({ code: '', name: '', eventAppIcon: '', iconColor: '' });
    setEditingId(null);
    setErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (eventType) => {
    setFormState({
      code: eventType.code || '',
      name: eventType.name || '',
      eventAppIcon: eventType.eventAppIcon || '',
      iconColor: eventType.iconColor || '',
    });
    setEditingId(eventType.id);
    setShowModal(true);
  };

  const hasDuplicate = (fieldName) => {
    const value = formState[fieldName]?.trim().toLowerCase();
    if (!value) return false;

    return eventTypes.some(
      (item) => item.id !== editingId && item[fieldName]?.trim().toLowerCase() === value,
    );
  };

  const handleSave = async () => {
    const isValid = validate();
    const duplicateErrors = {};

    if (hasDuplicate('code')) {
      duplicateErrors.code = 'Code must be unique';
    }

    if (hasDuplicate('name')) {
      duplicateErrors.name = 'Name must be unique';
    }

    if (Object.keys(duplicateErrors).length) {
      setErrors((prev) => ({ ...prev, ...duplicateErrors }));
      return;
    }

    if (!isValid) return;

    const payload = {
      code: formState.code?.trim(),
      name: formState.name?.trim(),
      eventAppIcon: formState.eventAppIcon?.trim() || '',
      iconColor: formState.iconColor?.trim() || '',
    };

    try {
      const savedType = editingId
        ? await updateEventType(editingId, payload)
        : await createEventType(payload);

      setShowModal(false);
      resetForm();
      resetDataTable();

      if (savedType) {
        setEventTypes((prev) => {
          if (editingId) {
            return sortTypes(prev.map((type) => (type.id === savedType.id ? savedType : type)));
          }
          return sortTypes([...prev, savedType]);
        });
      } else {
        fetchEventTypes();
      }
    } catch (error) {
      console.error('Error saving event type', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;

    try {
      await deleteEventType(id);
      resetDataTable();
      setEventTypes((prev) => prev.filter((type) => type.id !== id));
    } catch (error) {
      console.error('Error deleting event type', error);
    }
  };

  const canCreate = userProfile?.privileges?.includes('event-type:create');
  const canUpdate = userProfile?.privileges?.includes('event-type:update');
  const canDelete = userProfile?.privileges?.includes('event-type:delete');

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {canCreate && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Event Type
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm'>
          <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>App Icon</th>
                <th>Icon Colour</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {eventTypes.length === 0 && (
                <tr>
                  <td colSpan='5' className='text-center py-4 rounded-0'>No Event Types Setup</td>
                </tr>
              )}
              {eventTypes.map((eventType) => (
                <tr key={eventType.id}>
                  <td><strong>{eventType.code}</strong></td>
                  <td>{eventType.name}</td>
                  <td>{eventType.eventAppIcon || ''}</td>
                  <td>
                    {eventType.iconColor && (
                      <span
                        className={`d-inline-block rounded-1 me-8 bg-${eventType.iconColor}`}
                        style={{ width: '10px', height: '10px', position: 'relative',top: '2px' }}
                      />
                    )}
                    {eventType.iconColor || ''}
                  </td>
                  <td>
                    <div className='d-flex gap-2'>
                      {canUpdate && (
                        <button
                          className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleEdit(eventType)}
                        >
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleDelete(eventType.id)}
                        >
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Event Type' : 'New Event Type'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Code *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    name='code'
                    value={formState.code}
                    onChange={handleInputChange}
                  />
                  {errors.code && <div className='invalid-feedback'>{errors.code}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Name *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name='name'
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label'>App Icon</label>
                  <input
                    type='text'
                    className='form-control'
                    name='eventAppIcon'
                    value={formState.eventAppIcon}
                    onChange={handleInputChange}
                  />
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Icon Colour</label>
                  <input
                    type='text'
                    className='form-control'
                    name='iconColor'
                    value={formState.iconColor}
                    onChange={handleInputChange}
                    placeholder='Bootstrap color description - ie danger, success etc.'
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Close
                </button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTypeLayer;
